class PlantingGuide extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-guide-card]'));
    this.searchInput = this.querySelector('[data-guide-search]');
    this.filterSelect = this.querySelector('[data-guide-filter]');
    this.pagination = this.querySelector('[data-guide-pagination]');
    this.pagesContainer = this.querySelector('[data-guide-pages]');
    this.previousButton = this.querySelector('[data-guide-previous]');
    this.nextButton = this.querySelector('[data-guide-next]');
    this.emptyState = this.querySelector('[data-guide-empty]');
    this.status = this.querySelector('[data-guide-status]');
    this.currentPage = 1;
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    const initialQuery = new URLSearchParams(window.location.search).get('q');
    if (initialQuery && this.searchInput) this.searchInput.value = initialQuery;

    this.populateCategories();
    this.bindEvents();
    this.setAttribute('ready', '');
    this.render();
  }

  populateCategories() {
    if (!this.filterSelect) return;

    const categories = [
      ...new Set(
        this.cards
          .map((card) => card.dataset.category?.trim())
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));

    categories.forEach((category) => {
      const option = document.createElement('option');
      option.value = category;
      option.textContent = category;
      this.filterSelect.append(option);
    });
  }

  bindEvents() {
    this.searchInput?.addEventListener('input', () => {
      this.currentPage = 1;
      this.render();
    });

    this.filterSelect?.addEventListener('change', () => {
      this.currentPage = 1;
      this.render();
    });

    this.previousButton?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
    this.nextButton?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
    this.mobileQuery.addEventListener('change', () => {
      this.currentPage = 1;
      this.render();
    });
  }

  get pageSize() {
    const value = this.mobileQuery.matches
      ? this.dataset.pageSizeMobile
      : this.dataset.pageSizeDesktop;
    return Math.max(1, Number.parseInt(value || '12', 10));
  }

  get filteredCards() {
    const query = this.searchInput?.value.trim().toLocaleLowerCase() || '';
    const category = this.filterSelect?.value || '';

    return this.cards
      .filter((card) => {
        const searchableText = `${card.dataset.title || ''} ${card.dataset.description || ''}`;
        const matchesSearch = !query || searchableText.includes(query);
        const matchesCategory = !category || card.dataset.category === category;
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => Number(a.dataset.sort || 9999) - Number(b.dataset.sort || 9999));
  }

  render() {
    const cards = this.filteredCards;
    const pageCount = Math.max(1, Math.ceil(cards.length / this.pageSize));
    this.currentPage = Math.min(this.currentPage, pageCount);

    const start = (this.currentPage - 1) * this.pageSize;
    const visibleCards = new Set(cards.slice(start, start + this.pageSize));

    this.cards.forEach((card) => {
      card.hidden = !visibleCards.has(card);
    });

    if (this.emptyState) this.emptyState.hidden = cards.length > 0;
    if (this.pagination) this.pagination.hidden = cards.length <= this.pageSize;
    if (this.previousButton) this.previousButton.disabled = this.currentPage === 1;
    if (this.nextButton) this.nextButton.disabled = this.currentPage === pageCount;

    this.renderPageNumbers(pageCount);

    if (this.status) {
      this.status.textContent = `${cards.length} planting ${cards.length === 1 ? 'instruction' : 'instructions'} found. Page ${this.currentPage} of ${pageCount}.`;
    }
  }

  renderPageNumbers(pageCount) {
    if (!this.pagesContainer) return;
    this.pagesContainer.replaceChildren();

    this.getPageItems(pageCount).forEach((page) => {
      if (page === 'ellipsis') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'planting-guide__page-ellipsis';
        ellipsis.textContent = '…';
        ellipsis.setAttribute('aria-hidden', 'true');
        this.pagesContainer.append(ellipsis);
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'planting-guide__page-number';
      button.textContent = page;
      button.setAttribute('aria-label', `Go to page ${page}`);

      if (page === this.currentPage) {
        button.setAttribute('aria-current', 'page');
      }

      button.addEventListener('click', () => this.goToPage(page));
      this.pagesContainer.append(button);
    });
  }

  getPageItems(pageCount) {
    if (pageCount <= 6) {
      return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    if (this.currentPage <= 4) return [1, 2, 3, 4, 5, 'ellipsis', pageCount];
    if (this.currentPage >= pageCount - 3) {
      return [1, 'ellipsis', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
    }

    return [1, 'ellipsis', this.currentPage - 1, this.currentPage, this.currentPage + 1, 'ellipsis', pageCount];
  }

  goToPage(page) {
    const pageCount = Math.max(1, Math.ceil(this.filteredCards.length / this.pageSize));
    const nextPage = Math.min(Math.max(1, page), pageCount);
    if (nextPage === this.currentPage) return;

    this.currentPage = nextPage;
    this.render();
    this.querySelector('[data-guide-grid]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('planting-guide')) {
  customElements.define('planting-guide', PlantingGuide);
}
